import React, { useEffect, useState } from 'react';
import {
  Button, Chip, CircularProgress, IconButton, MenuItem, Stack, TextField, Tooltip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import iziToast from 'izitoast';

import { createStudentComment, deleteStudentComment, getStudentComments } from '../../actions/journal';
import { internalServerError } from '../../utils/consts/pathRoutes';
import { PERMISSIONS, useCurrentUser } from '../../utils/currentUser';

// Виды из требований: бэкграунд при поступлении, заметка об уровне, причины перевода
// и отчисления. Последние два проставляются системой при самих действиях.
const KINDS = ['Общий', 'Поступление', 'Уровень'];
const SYSTEM_KINDS = ['Перевод', 'Отчисление'];

const KIND_COLOR = {
  'Перевод': 'info',
  'Отчисление': 'error',
};

const formatMoment = (value) => new Date(value).toLocaleString('ru-RU', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

/**
 * Комментарии по слушателю.
 *
 * Заказчик возвращался к ним всю встречу: бэкграунд при поступлении, на чём остановился
 * предыдущий преподаватель, почему человек оказался в другой группе. Привязаны к
 * слушателю, поэтому при переводе достаются новому преподавателю вместе с ним.
 *
 * Переводы и отчисления попадают сюда сами — отдельно их писать не нужно.
 */
const StudentComments = ({ studentId }) => {
  const { can } = useCurrentUser();
  const canWrite = can(PERMISSIONS.JOURNAL_MANAGE) || can(PERMISSIONS.JOURNAL_MARK_WRITE);
  const canDelete = can(PERMISSIONS.JOURNAL_MANAGE);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [kind, setKind] = useState('Общий');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getStudentComments(studentId)
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [studentId]);

  const submit = () => {
    setSaving(true);
    createStudentComment(studentId, { kind, body })
      .then(() => { setBody(''); load(); })
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }))
      .finally(() => setSaving(false));
  };

  const remove = (comment) => {
    deleteStudentComment(comment.id)
      .then(load)
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }));
  };

  return (
    <div style={{ marginTop: 24 }}>
      <p className="journal-subtitle">Комментарии</p>

      {canWrite && (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <TextField
            select size="small" label="Вид" value={kind}
            onChange={(event) => setKind(event.target.value)} sx={{ width: 170 }}
          >
            {KINDS.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
          </TextField>

          <TextField
            size="small" label="Комментарий" value={body} multiline maxRows={4}
            onChange={(event) => setBody(event.target.value)} sx={{ flex: 1, minWidth: 320 }}
            placeholder="Например: учился в СПб полгода, не с нуля"
          />

          <Button
            variant="contained" onClick={submit}
            disabled={saving || !body.trim()} sx={{ mt: 0.5 }}
          >
            Добавить
          </Button>
        </Stack>
      )}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size={20} color="warning" /></Stack>
      ) : items.length === 0 ? (
        <div className="journal-muted">Комментариев нет.</div>
      ) : (
        <Stack spacing={1}>
          {items.map((comment) => (
            <div key={comment.id} className="journal-comment">
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Chip
                  size="small" label={comment.kind}
                  color={KIND_COLOR[comment.kind] || 'default'}
                  variant={SYSTEM_KINDS.includes(comment.kind) ? 'filled' : 'outlined'}
                />
                {comment.group_name && <Chip size="small" variant="outlined" label={comment.group_name} />}
                <span className="journal-muted">
                  {comment.author_name || 'система'} · {formatMoment(comment.created_at)}
                </span>

                {canDelete && (
                  <Tooltip title="Удалить">
                    <IconButton size="small" sx={{ ml: 'auto' }} onClick={() => remove(comment)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              <div style={{ whiteSpace: 'pre-wrap' }}>{comment.body}</div>
            </div>
          ))}
        </Stack>
      )}
    </div>
  );
};

export default StudentComments;
